import { NavMenuQueryResult, PageQueryResult } from "@shared/sanity/types";
import type { Guide } from "@/sanity/queries/types/GuideBlocksTypes";

const createApiClient = () => {
  const getBaseUrl = () => {
    const baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL;
    if (!baseUrl) {
      throw new Error("API base URL is not configured");
    }
    return baseUrl;
  };

  const fetchData = async <T>({
    endpoint,
    options = {},
    errorPrefix = "API Error",
  }: {
    endpoint: string;
    options?: RequestInit;
    notFoundHandler?: (() => T | null) | null;
    errorPrefix?: string;
  }): Promise<T | null> => {
    try {
      const baseUrl = getBaseUrl();
      const fullUrl = `${baseUrl}${endpoint}`;

      const response = await fetch(fullUrl, {
        ...options,
      });

      if (response.status === 404) {
        console.log("404 returned for:", fullUrl);
        return null;
      }

      if (!response.ok) {
        throw new Error(`${errorPrefix}: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  };

  return { fetchData };
};

/* DATA FETCHING FUNCTIONS*/

export async function getPage(slug: string): Promise<PageQueryResult | null> {
  const apiClient = createApiClient();
  return apiClient.fetchData({
    endpoint: `/page/${slug}`,
    options: {
      next: { revalidate: 60 },
    },
    errorPrefix: "page not found",
  });
}

export async function getNavMenu(slug: string): Promise<NavMenuQueryResult | null> {
  const apiClient = createApiClient();
  return apiClient.fetchData({
    endpoint: `/navigationMenu/${slug}`,
    options: {
      next: { revalidate: 60 },
    },
    errorPrefix: "Navigation menu not found",
  });
}

export async function getGuide(slug: string): Promise<Guide | null> {
  const apiClient = createApiClient();
  return apiClient.fetchData<Guide>({
    endpoint: `/guide/${slug}`,
    errorPrefix: "Guide not found",
    options: {
      next: { revalidate: 60 },
    },
  });
}
